import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import ErrorBoundary from "@/components/ErrorBoundary";
import DashboardLayout from "@/components/DashboardLayout";
import PublicLayout from "@/components/layouts/PublicLayout";
import { publicRoutes, wrappedDashboardRoutes, RouteWithPath } from "@/routes/routeConfig";
import GlobalErrorBoundary from '@/components/system/GlobalErrorBoundary';
import { LoadingState } from '@/components/ui/loading-state';

// Load the Dashboard component directly to ensure it's available
import Dashboard from '@/pages/Dashboard';

// Standard fallback for routes
const RouteLoadingFallback = () => (
  <LoadingState 
    message="Loading page..." 
    centered={true}
    size="default"
  />
);

/**
 * Optimized AppRoutes component
 * Using standardized lazy loading and improved performance tracking
 */
const AppRoutes: React.FC = () => {
  const location = useLocation();
  
  return (
    <GlobalErrorBoundary>
      <ErrorBoundary componentName="AppRoutes">
        <Routes>
          {/* Public routes that should never be protected */}
          {publicRoutes.map((route: RouteWithPath) => (
            <Route
              key={route.path}
              path={route.path}
              element={
                <ErrorBoundary componentName={`PublicRoute:${route.path}`}>
                  {route.path === "/signin" || route.path === "/signup" ? (
                    route.element
                  ) : (
                    <PublicLayout noFooter={route.noFooter}>
                      {route.element}
                    </PublicLayout>
                  )}
                </ErrorBoundary>
              }
            />
          ))}
          
          {/* Root path - always redirect to dashboard for now to ensure content is visible */}
          <Route
            path="/"
            element={<Navigate to="/dashboard" replace />}
          />
          
          {/* Dashboard components with persistent DashboardLayout */}
          <Route element={<DashboardLayout />}>
            {/* Dashboard route - directly loaded for better reliability */}
            <Route 
              path="/dashboard" 
              element={
                <ErrorBoundary componentName="DashboardRoute">
                  <Dashboard />
                </ErrorBoundary>
              } 
            />
            
            {/* Other dashboard routes */}
            {wrappedDashboardRoutes.map((route: RouteWithPath) => (
              <Route 
                key={route.path}
                path={route.path} 
                element={
                  <ErrorBoundary componentName={`DashboardRoute:${route.path}`}>
                    <Suspense fallback={<RouteLoadingFallback />}>
                      {route.element}
                    </Suspense>
                  </ErrorBoundary>
                }
              />
            ))}
          </Route>
          
          {/* Fallback redirect for unknown routes */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </ErrorBoundary>
    </GlobalErrorBoundary>
  );
};

export default React.memo(AppRoutes);
